import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { fetchApi } from "../../Functions";
import { addSingleTaskLink, updateSingleTaskLink, removeSingleTaskLink } from "../../../store/InspectionTaskLinks";
import { TInspectionTaskLink } from "../../../types/types";
import { URLs } from "../../../Constants/Consts";

export type TaskLinkFormData = {
    linkUrl: string;
    linkText: string;
    linkCategory: string;
};

/**
 * Hook for CRUD operations on inspection task links
 * Provides add, edit, and remove functions with local Redux updates
 */
export function useInspectionTaskLinkCRUD(activeInspectionTaskId: number | null) {
    const dispatch = useDispatch();

    // Helper to build link payload
    const buildLinkPayload = useCallback((data: TaskLinkFormData, itLinkId = 0) => ({
        itLinkId,
        inspectionTaskId: activeInspectionTaskId,
        linkUrl: data.linkUrl,
        linkText: data.linkText,
        linkCategory: data.linkCategory
    }), [activeInspectionTaskId]);

    // Add task link
    const addTaskLink = useCallback(async (data: TaskLinkFormData): Promise<boolean> => {
        if (!activeInspectionTaskId) {
            toast.error("No active task selected");
            return false;
        }

        try {
            const res = await fetchApi(URLs.addInspectionTaskLink(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildLinkPayload(data))
            });
            const newLink = await res.json();

            dispatch(addSingleTaskLink({
                itLinkId: newLink?.itemId,
                inspectionTaskId: activeInspectionTaskId,
                linkUrl: data.linkUrl,
                linkText: data.linkText,
                linkCategory: data.linkCategory,
            }));
            toast.success("Task Link added successfully");
            return true;
        } catch (error) {
            console.error("Error adding task link:", error);
            toast.error("Failed to add Task Link");
            return false;
        }
    }, [activeInspectionTaskId, buildLinkPayload, dispatch]);

    // Edit task link
    const editTaskLink = useCallback(async (itLinkId: number, data: TaskLinkFormData): Promise<boolean> => {
        if (!activeInspectionTaskId) {
            toast.error("No active task selected");
            return false;
        }

        try {
            await fetchApi(URLs.editDeleteInspectionTaskLink(itLinkId), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildLinkPayload(data, itLinkId))
            });

            dispatch(updateSingleTaskLink({
                itLinkId,
                inspectionTaskId: activeInspectionTaskId,
                linkUrl: data.linkUrl,
                linkText: data.linkText,
                linkCategory: data.linkCategory,
            }));

            toast.success("Task Link updated successfully");
            return true;
        } catch {
            toast.error("Failed to edit Task Link");
            return false;
        }
    }, [activeInspectionTaskId, buildLinkPayload]);

    // Remove task link
    const removeTaskLink = useCallback(async (link: TInspectionTaskLink): Promise<boolean> => {
        try {
            await fetchApi(URLs.editDeleteInspectionTaskLink(link.itLinkId), { method: 'DELETE' });
            dispatch(removeSingleTaskLink(link.itLinkId));
            toast.success("Task Link deleted");
            return true;
        } catch (error) {
            const err = error as { detail?: string };
            toast.error(err?.detail || "Failed to remove task link");
            return false;
        }
    }, [dispatch]);

    return {
        addTaskLink,
        editTaskLink,
        removeTaskLink
    };
}
